import {
  App, Button, Row, Col, Modal, Typography, Tooltip, 
} from 'antd';
import {
    CheckCircleFilled, CloseCircleFilled,
    EditOutlined, QuestionCircleFilled, ExclamationCircleFilled, 
} from '@ant-design/icons';
import React from "react";
import { useTranslation } from "react-i18next";
import { submitDriverReview } from "../../api/maintenance";
import { showApiError } from "../../utility/error/error-handler";
import DataCards from '../../components/data/DataCards';
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";


// A job can have items previously requested and previously reviewed by the driver
// This process has to distinguish between current request/driver review AND previously reviewed items
const JobAwaitingReview = ({vehicle, selectedReviewRequest, setSelectedReviewRequest, editJobTask, jobItemStatuses, setRefreshPage, setSubmitProgress, submitProgress}) => {

  const { t } = useTranslation();
  const { isOnline } = useOnlineStatus();
  const { Text } = Typography;
  const { confirm } = Modal;

  const { message, notification } = App.useApp();

  const driverApproved = 'DRIVER_APPROVED';
  const driverRejected = 'DRIVER_REJECTED'; 
  const customerApproved = jobItemStatuses.find(status => status.key === 'CUSTOMER_APPROVED');
  const customerRejected = jobItemStatuses.find(status => status.key === 'CUSTOMER_REJECTED');
  const awaitingReview = jobItemStatuses.find(status => status.key === 'CUSTOMER_APPROVAL_REQUESTED');
  const authorised = jobItemStatuses.find(status => status.key === 'AUTHORISED');
  const declined = jobItemStatuses.find(status => status.key === 'DECLINED');
  const reviewedAuthorised = jobItemStatuses.find(status => status.key === 'REVIEWED_AUTHORISED');
  const reviewedDeclined = jobItemStatuses.find(status => status.key === 'NOT_DONE');
  const notDone = jobItemStatuses.find(status => status.key === 'REVIEWED_DECLINED');
  const isAwaitingReview = selectedReviewRequest?.items.length > 0  && selectedReviewRequest?.items.find(item => item.status.id === awaitingReview.id) ? true : false;
  const hasReviews = selectedReviewRequest?.items.length > 0  && selectedReviewRequest?.items.find(item => item.status === driverApproved || item.status === driverRejected) ? true : false;
  
  // This process has to distinguish between current request/driver review AND previously reviewed items
  const itemsBeingReviewed = selectedReviewRequest?.items.length > 0 ? selectedReviewRequest?.items.filter(item => item.status.id === awaitingReview.id || 
                                                                                                                   item.status === driverApproved || 
                                                                                                                   item.status === driverRejected) : null;
  const itemsPreviouslyReviewed = selectedReviewRequest?.items.length > 0 ? selectedReviewRequest?.items.filter(item => item.status.id !== awaitingReview.id && 
                                                                                                                item.status !== driverApproved && 
                                                                                                                item.status !== driverRejected)
                                                                                                        .sort((a, b) => ((a.status.id < b.status.id) ? -1 : 0)) : null;
                                                                                                       

  const ReviewStatusField = ({status}) => {
    let icon = <QuestionCircleFilled style={{color:'var(--dp-status-colour-warning)', fontSize:'24px'}}/>;
 
    if (status === driverApproved || status.id === customerApproved.id || status.id === authorised.id || status.id === reviewedAuthorised.id)
      {
         icon = <CheckCircleFilled style={{color:'var(--dp-status-colour-ok)', fontSize:'24px'}}/>
      } 
    else if(status === driverRejected || status.id === customerRejected.id || status.id === declined.id || status.id === reviewedDeclined.id || 
            status.id === notDone.id) 
      {  
         icon = <CloseCircleFilled style={{color:'var(--dp-status-colour-danger)', fontSize:'24px'}}/>
      }
  
    return (
             <Tooltip title={status.description}>{icon}</Tooltip>
           )
  }

  const EditTaskIcon = ({status, review}) => {
    let showEditIcon = (status.id === awaitingReview.id || status === driverApproved || status === driverRejected);

    return (
            (showEditIcon)
            ?
            <EditOutlined 
              style={{ fontSize:'20px' }} //in keeping with default DataCards edit button
              onClick={() => editJobTask(review)}
              />
            :
            <></>
           )
  }
  
  const header = (review) => {
    return review?.description;
}

const detail = (review) => {

  return (
    <Row style={{ 'width': '100%' }} align="middle" justify="space-between">
      <Col>
        <Text>{ t('field.recharge') }: {review.totalRechargeCost}</Text>
      </Col>
      <Col>
          <table>
              <tbody>
                <tr>
                    <td>
                        <ReviewStatusField status={review.status} />
                    </td>
                    <td style={{ width: '30px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <EditTaskIcon status={review.status} review={review} />
                        </div>
                    </td>
                </tr>
              </tbody>
          </table>
      </Col>       
    </Row>
  );
}

  function PendingJobRequestCardView({pendingReview, editJobTask}) {
    // Variable Styling
    let titleColour = 'var(--dp-panel-text-colour)';

    return (
             (pendingReview)
             ?
             <div>
                <Row>
                    <Col> 
                      <Row><Text className='dp-field-label'>{t('field.reference')}</Text></Row>
                      <Row><Text>{pendingReview.reference}</Text></Row>
                      <Row><Text className='dp-field-label'>{t('field.date')}</Text></Row>
                      <Row><Text>{pendingReview.arrivalDate}</Text></Row>
                      <Row><Text className='dp-field-label'>{t('field.estimated_completion')}</Text></Row>
                      <Row><Text>{pendingReview.estimatedCompletionDate}</Text></Row>
                      <Row><Text className='dp-field-label'>{t('field.odo_reading')}</Text></Row>
                      <Row><Text>{pendingReview.odoReading}</Text></Row>
                      <Row><Text className='dp-field-label'>{t('field.recharge_total')}</Text></Row>
                      <Row><Text>{pendingReview.rechargeCost}</Text></Row>          
                    </Col>
                </Row>
                {
                  itemsBeingReviewed?.length > 0
                  ?
                  <DataCards items={itemsBeingReviewed}
                             header={header}
                             detail={detail}> 
                  </DataCards>
                  :
                  <></>
                }
                {
                  itemsPreviouslyReviewed?.length > 0 
                  ?
                  <div>
                    <Row>
                      <Col>
                         <Text className='modal-popup-subheader' style={{ color: titleColour, display: 'inline-block', marginTop: '10px' }}> {t('title.previously_reviewed')} </Text>
                      </Col>
                    </Row>
                    
                    <DataCards items={itemsPreviouslyReviewed}
                               header={header}
                               detail={detail}
                    > 
                    </DataCards>
                  </div>
                  :
                  <></>
                }
            </div>
            :
            <div></div>
           )
  }

  function ApprovedRecharge({jobItems}) {
    let reviewedRechargeTotal = 0;
    
    if (jobItems?.length > 0)
    {
      jobItems.forEach(item => {
        if (item.status === driverApproved || item.status.id === customerApproved.id || item.status.id === authorised.id || item.status.id === reviewedAuthorised.id)
        {
          reviewedRechargeTotal = reviewedRechargeTotal + item.totalRechargeCost;
        }
      })
      reviewedRechargeTotal = reviewedRechargeTotal?.toFixed(2);
    }
    
    return (
             <div>
                <Row align="left">
                  <Text className='dp-field-label'>{t('field.approved_recharge_total')}</Text>
                </Row>
                <Row>
                  {reviewedRechargeTotal}
                </Row>
              </div>
    )
  }

  const submitReview = (approveAll) => {
    // Be advised, a job can have items previously requested and previously reviewed by the driver
    // Only items actually reviewed by the driver should be included in this request for back-office review
    // The below .filter code creates a new object with ONLY those items reviewed or pending driver review as part of this request
    const selectedDriverReviewRequest = {
      ...selectedReviewRequest, 
      items: selectedReviewRequest.items.filter(item => item.status === driverApproved || item.status === driverRejected || item.status.id === awaitingReview.id)
    };
    
    selectedDriverReviewRequest?.items.forEach(element => {
      const driverReviewed = element.status === driverApproved ||  element.status === driverRejected;
      const driverReviewPending = element.status.id === awaitingReview.id;

      if (approveAll)
      {
        // if as part of this review request, driver manually reviewed OR awaiting driver review AND the driver selected Approve All button
        if (driverReviewed || driverReviewPending)
        {
          if (element.status === driverRejected) {
            element.reviewNote = null;
            element.reviewOutcome = 'APPROVE';
          }
          element.status = customerApproved;    
        }
      }
      else {
        if (driverReviewed) {
          element.status = element.status === driverApproved ? customerApproved : customerRejected;   
        }
      }
    });

    submitDriverReview(vehicle.vehicleId, selectedDriverReviewRequest, setSubmitProgress)
    .then(res =>{
       message.success(t('message.successfully_submitted_review', {0: selectedReviewRequest.reference}));
       setSelectedReviewRequest(null);  // Closes the popup
       setRefreshPage(true);
    })
    .catch(err => showApiError(t('title.review_jobs'), err, notification));
  }

  const showConfirmPopup = () => {
    confirm({
      title: t('message.are_you_sure'),
      icon: <ExclamationCircleFilled />,
      content: t('message.overwriting_review'),
      okText: t('action.confirm'),
      okType: 'primary',
      onOk() {
        submitReview(true);
      },
      onCancel() {
        // console.log('Cancel');
      },
    });
  };

return (
         <Modal className="std-portal-popup dp-wrap-card-title"
                open={selectedReviewRequest}
                title={t('title.review_jobs')}
                closable={true}
                keyboard={true}
                footer={
                          isOnline
                          ?
                          [
                            <ApprovedRecharge jobItems={selectedReviewRequest?.items}></ApprovedRecharge>,
                            <Button key="approveall" 
                                    loading={submitProgress < 100}
                                    onClick={(e) => {
                                      if (hasReviews)
                                      {
                                        showConfirmPopup();
                                      }
                                      else
                                      {
                                        submitReview(true);
                                      }
                                    }}
                            >
                              {t('action.approve_all')}
                            </Button>,
                            <Button type="primary" 
                                    disabled={isAwaitingReview}
                                    loading={submitProgress < 100}
                                    onClick={(e) => {
                                        submitReview(false);
                                      }}
                            >
                              {t('action.submit_review')}
                            </Button>
                          ]
                          :
                          <></>
                       }
                onCancel={() => {
                    setSelectedReviewRequest(null);
                }}
         >
            <PendingJobRequestCardView pendingReview={selectedReviewRequest} editJobTask={editJobTask}/>
         </Modal>     
       );
}

export default JobAwaitingReview;